import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { themeColors } from 'utils/tailwind/tailwindHelpers';
import * as yup from 'yup';

import { JobAddedSuccess } from '@/modules/settings/property';
import {
  useCreateCustomOperations,
  useGetPropertyCrops,
  useGetSuggestedCustomOperations,
} from '@/modules/settings/property/apiHooks';
import { IPropertySettings } from '@/modules/settings/property/types';
import {
  Button,
  InputField,
  SelectField,
  Typography,
} from '@/shared/components/atoms';
import { SuggestionsList } from '@/shared/components/molecules/SuggestionsList';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';
import useDebounce from '@/shared/hooks/useDebounce';
import { IGeneral } from '@/shared/types';

interface IFormSchema extends IPropertySettings.ICustomOperationFormSchema {
  selectedCropId?: number;
}

const AddEditCustomOperation = ({
  cropId,
  operation,
  onModalClose,
  onSuccessFunction,
  showCropSelection = false,
}: {
  cropId: number;
  operation?: IPropertySettings.IPropertyJob;
  onModalClose?: () => void;
  onSuccessFunction?: () => void;
  showCropSelection?: boolean;
}) => {
  const { t } = useTranslation('common');
  const { pathname, query } = useRouter();
  const divRef = useRef<HTMLDivElement>(null);
  const { openModal, closeModal } = useModal();
  const [search, setSearch] = useState<string | undefined>('');
  const [showList, setShowList] = useState<boolean>(true);
  const [selectedOperation, setSelectedOperation] = useState('');
  const [selectedAsset] = useStore((store) => store?.asset);
  const [isDemoFarm] = useStore((store) => store?.isDemoFarm);
  const { id: propertyId } = query;

  const usedPropertyId: number | undefined = useMemo(
    () =>
      pathname === '/settings/property/[id]'
        ? Number(propertyId)
        : selectedAsset?.id,
    [pathname, selectedAsset?.id, propertyId]
  );

  const { data } = useGetPropertyCrops({
    propertyId: selectedAsset?.id,
    pageNumber: 1,
    pageSize: 100000,
  });

  const crops = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data]
  );

  // TODO: add mutation for edit API
  const { mutate } = useCreateCustomOperations();

  const schema = yup.object({
    taskName: yup
      .string()
      .required(t('this-field-is-required'))
      .min(1, t('this-field-is-required')),
    notes: yup.string(),
    ...(showCropSelection && {
      selectedCropId: yup
        .number()
        .required(t('this-field-is-required'))
        .typeError(t('this-field-is-required')),
    }),
  });

  const formMethods = useForm<IFormSchema>({
    resolver: yupResolver(schema),
  });

  const {
    watch,
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = formMethods;

  const taskNameWatcher = watch('taskName');
  const selectedCropId = watch('selectedCropId');
  const debouncedSearch = useDebounce(taskNameWatcher, 500);

  const suggestedCustomOperationsList = useGetSuggestedCustomOperations({
    cropId: selectedCropId ?? cropId,
    entityId: usedPropertyId,
    search,
  });

  useEffect(() => {
    if (debouncedSearch && debouncedSearch !== selectedOperation) {
      setShowList(true);
      setSearch(debouncedSearch);
      setSelectedOperation('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleNameSelection = (name: string) => {
    setSelectedOperation(name);
    setValue('taskName', name);
    setShowList(false);
    setSearch(undefined);
  };

  const onSubmit = (data: IFormSchema) => {
    if (usedPropertyId && !isDemoFarm) {
      const createOperationData: IPropertySettings.ICreateCustomOperationSchema =
        {
          taskName: data.taskName,
          entityId: usedPropertyId,
          cropId: showCropSelection ? data.selectedCropId ?? cropId : cropId,
          ...(data.notes && { notes: data.notes }),
        };

      mutate(createOperationData, {
        onSuccess: () => {
          if (onModalClose) onModalClose();
          if (onSuccessFunction) onSuccessFunction();
          closeModal();
          if (!showCropSelection)
            openModal(<JobAddedSuccess />, {
              dismissable: false,
            });
        },
      });
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='flex flex-col gap-16'>
          {showCropSelection && (
            <SelectField
              id='selectedCropId'
              required
              borderColor={themeColors().grey[200]}
              borderRadius='1rem'
              width='w-full'
              py='0.5rem'
              px='0.5rem'
              label={t('crop')}
              placeholder={t('crop')}
              errorMessageId={errors.selectedCropId?.message}
              className='!rounded-xl bg-background-transparent'
              list={crops}
              getLabel={(item: IGeneral.ICropSchema) => item.name}
              getValue={(item: IGeneral.ICropSchema) => item.id}
            />
          )}
          <div className='relative' ref={divRef}>
            {/* @TODO : to add autocomplete textbox to our design system instead of having input field and suggestions list sepeparated */}
            <InputField
              defaultValue={operation?.name}
              borderColor='border-outline-variant'
              width='w-full'
              py='!py-16'
              label={t('custom-operation-name')}
              placeholder={t('custom-operation-name')}
              error={!!errors.taskName}
              required
              errorMessage={errors.taskName?.message}
              className='!rounded-xl bg-background-transparent'
              {...register('taskName')}
            />
            <Typography variant='body2' className='mt-4 ml-1 text-surface-10'>
              {t('choose-or-enter-operation')}
            </Typography>
            {search && showList && (
              <SuggestionsList
                requestInfo={suggestedCustomOperationsList}
                divRef={divRef}
                setNameValue={handleNameSelection}
                placeholder={t('suggested-operations')}
              />
            )}
          </div>
          <InputField
            defaultValue={operation?.notes ?? undefined}
            borderColor='border-outline-variant'
            width='w-full'
            py='!py-16'
            label={`${t('operation-description')} (${t('optional')})`}
            placeholder={`${t('operation-description')} (${t('optional')})`}
            error={!!errors.notes}
            errorMessage={errors.notes?.message}
            className='!rounded-xl bg-background-transparent'
            {...register('notes')}
          />
        </div>
        <Button
          type='submit'
          disabled={!formMethods.formState.isValid}
          className='mt-32 h-56 !w-full !rounded-xl'
        >
          {t('confirm')}
        </Button>
      </form>
    </FormProvider>
  );
};

export { AddEditCustomOperation };
